<template>
	<div class="sr radius-small border-100 border-high-active bg-medium" @click="emits('onPosterClickOut')">
		<DefineVideoControls>
			<div class="video-controls">
				<button class="mute" @click.stop="toggleMute">
					<img :src="volumeIcon" alt="volume" height="20" />
				</button>

				<button class="fullscreen" @click.stop="requestFullscreen">
					<img :src="fullscreenIcon" alt="fullscreen" height="20" />
				</button>
			</div>
		</DefineVideoControls>

		<div class="sr__container">
			<div class="provider-icon sm:hidden">
				<slot name="provider-icon"></slot>
			</div>

			<div class="sr__content">
				<div class="sr__titlePoster radius-small mx-5 sm:mx-0">
					<PictureComp
						v-if="posterSRImage"
						:alt="campaignNode?.content.title"
						:sponsoredRecImages="posterSRImage"
						image-profile="poster"
						@imageError="src => $emit('imageError', src)"
					/>
				</div>

				<div class="sr__titleContent g-6">
					<div class="sr__providerWrapper sm:show">
						<slot name="provider-icon"></slot>
					</div>

					<slot name="campaign-info">
						<div class="sr__titleInfo g-4">
							<h2 class="sr__title text-high align-content m-0" data-testid="sr__title">
								{{ promotionalTitle }}
							</h2>

							<div
								v-if="promotionalText"
								class="sr__promoText text-high align-content"
								:class="{
									'sm:show': hasTimeRemaining && getReleaseDate,
								}"
							>
								{{ promotionalText }}
							</div>

							<CountdownTimer
								v-if="hasTimeRemaining && getReleaseDate"
								:dateTime="getReleaseDate"
								class="align-content"
							/>
						</div>
					</slot>

					<div class="align-content">
						<slot name="action-items"></slot>
						<div v-if="disclaimerText" class="sr__disclaimer pt-2">
							{{ disclaimerText }}
						</div>
					</div>
				</div>

				<div class="sr__video-container" :class="{ 'lighter-gradient': useLighterGradient }">
					<video
						ref="videoElement"
						class="title-video__video sr__video-container__video"
						width="100%"
						autoplay
						muted
						playsinline
						loop
						@loadedmetadata="setVideoDuration"
						@timeupdate="onTimeUpdate"
						@ended="onVideoEnded"
						@volumechange="onVolumeChange"
						@fullscreenchange="onChangeFullscreen"
						@imageError="$emit('imageError', sponsoredAd.campaign?.promotionalVideo?.url)"
					>
						<source :src="sponsoredAd.campaign?.promotionalVideo?.url || ''" type="video/mp4" />
					</video>

					<ReuseVideoControls />
				</div>

				<ReuseVideoControls />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

import CountdownTimer from '@/features/sponsored-recommendations/components/CountdownTimer.vue';
import PictureComp from '@/components/picture/PictureComp.vue';

import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { useCampaignInfo } from '@/features/sponsored-recommendations/composables/useSendBidId';

import { SraEventAction } from '@/@types/graphql-types';
import { ImpressionTrackingEvents } from '@/enums/events';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { Maybe } from '@/helpers/types';
import { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';
import { createReusableTemplate } from '@vueuse/core';

/** Viewing thresholds in percentages at which we want to track. */
type TrackingWatchedPointsInPercentage = (typeof TrackingWatchedPointsInPercentage)[number];
const TrackingWatchedPointsInPercentage = [0, 25, 50, 75, 90, 100] as const;

/** Viewing thresholds in seconds at which we want to track. */
type TrackingWatchedPointsInSeconds = (typeof TrackingWatchedPointsInSeconds)[number];
const TrackingWatchedPointsInSeconds = [2, 6, 15] as const;

const props = withDefaults(
	defineProps<{
		sponsoredAd: NonNullable<SponsoredAdFragment>;
		lazy?: boolean;
		additionalContexts?: SnowplowContext[];
	}>(),
	{
		lazy: true,
		additionalContexts: () => [],
	}
);

const emits = defineEmits<{
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
}>();

const { jwConfig } = useRuntimeConfig().public;

const { campaignNode, promotionalTitle, promotionalText } = useCampaignInfo(() => props.sponsoredAd.campaign);

const { trackSraEvent } = useBackendTracking();

const disclaimerText = computed(() => props.sponsoredAd.campaign?.disclaimerText);

const getReleaseDate = computed<Date | undefined>(() => {
	if (!import.meta.client || !props.sponsoredAd.campaign?.countdownTimer) return undefined;

	return new Date(props.sponsoredAd.campaign?.countdownTimer);
});

// Title Poster (to support AVIF)
const posterSRImage = computed<PosterSponsoredRecImages | null>(() => {
	let posterUrl = props.sponsoredAd.campaign?.posterOverride || campaignNode?.value.content.posterUrl;
	if (!posterUrl) return null;

	if (posterUrl.startsWith('/')) {
		posterUrl = `${jwConfig.IMAGESCALER_URL}${posterUrl.replace('{profile}', 's332')}`;
	}

	return {
		imageUrl: posterUrl,
		sourceSets: [
			{
				srcSet: posterUrl.replace(/\.{?\w+}?$/, '.avif'),
				imageProp: {
					extension: '.avif',
					type: 'image/avif',
				},
				sourceProp: {
					viewport: 'desktop',
				},
				media: null,
			},
		],
	};
});

const timerToMatchStarts = setInterval(checkIfHasTimeRemaining, 1000);

// Manage check to display countdown
onMounted(() => checkIfHasTimeRemaining());
onUnmounted(() => clearInterval(timerToMatchStarts));

const hasTimeRemaining = ref(true);

function checkIfHasTimeRemaining() {
	// If there is no release time, hide countdown
	if (!import.meta.client || !getReleaseDate.value) {
		hasTimeRemaining.value = false;
		return;
	}

	const currentTime = new Date();

	// If release time is after the currentTime
	if (currentTime.getTime() >= getReleaseDate.value.getTime()) {
		hasTimeRemaining.value = false;
		return;
	}

	hasTimeRemaining.value = true;
}

// Manage Video
type WebKitHTMLVideoElement =
	| HTMLVideoElement
	// To support mobile safari browsers
	| (HTMLVideoElement & { webkitSupportsFullscreen: true; webkitEnterFullscreen: () => void });

const videoElement = ref<WebKitHTMLVideoElement | null>(null);
const isMuted = ref(videoElement.value?.muted ?? true);

const { define: DefineVideoControls, reuse: ReuseVideoControls } = createReusableTemplate();

const volumeIcon = computed(() => {
	const filename = isMuted.value ? 'volume-xmark' : 'volume-high';
	return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/${filename}.svg`;
});

const titleContext = computed(() => {
	return new SnowplowTitleContextGraphql(
		campaignNode.value?.objectId,
		campaignNode.value?.objectType,
		campaignNode.value?.__typename === 'Season' ? campaignNode.value?.content.seasonNumber : null
	);
});

function onVolumeChange(event: Event) {
	const change = (event.target as HTMLVideoElement).muted ? 'muted' : 'unmuted';
	isMuted.value = change === 'muted';

	if (!campaignNode.value) {
		return;
	}

	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'toggle_mute',
			value: isMuted.value ? 1 : 0,
		},
		[titleContext.value]
	);
}

function toggleMute() {
	isMuted.value = !isMuted.value;

	if (videoElement.value) {
		videoElement.value.muted = isMuted.value;
	}
}

/* Track Duration Play */
const videoDuration = ref(0);
const lastTrackedPointPercentage = ref<TrackingWatchedPointsInPercentage | -1>(-1);
const lastTrackedPointSeconds = ref<TrackingWatchedPointsInSeconds | -1>(-1);

function setVideoDuration() {
	if (!videoElement.value) return;
	videoDuration.value = videoElement.value.duration;
}

function onTimeUpdate() {
	const currentTime = videoElement.value?.currentTime ?? 0;

	/** Percentage Tracking */
	if (lastTrackedPointPercentage.value < 100 && videoDuration.value > 0) {
		const percentageViewed = Math.ceil((currentTime / videoDuration.value) * 100);
		const nextTrackingPointPercent = TrackingWatchedPointsInPercentage.find(
			point => point > lastTrackedPointPercentage.value
		);

		if (nextTrackingPointPercent != null && percentageViewed >= nextTrackingPointPercent) {
			lastTrackedPointPercentage.value = nextTrackingPointPercent;

			trackSraEvent({
				action: SraEventAction.VideoView,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointPercent,
			});
		}
	}

	/** Seconds Tracking */
	if (lastTrackedPointSeconds.value < 15) {
		const secondsViewed = Math.floor(currentTime);
		const nextTrackingPointSeconds = TrackingWatchedPointsInSeconds.find(
			point => point > lastTrackedPointSeconds.value
		);

		if (nextTrackingPointSeconds != null && secondsViewed >= nextTrackingPointSeconds) {
			lastTrackedPointSeconds.value = nextTrackingPointSeconds;

			trackSraEvent({
				action: SraEventAction.VideoViewSeconds,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointSeconds,
			});
		}
	}
}

/**
 * `this.onTimeUpdate` doesn't always fire at 100 before restarting the loop.
 * So we trigger it manually to guarantee it gets tracked.
 */
function onVideoEnded() {
	if (!videoElement.value) return;
	videoElement.value.play();
}

const fullscreenIcon = computed(() => {
	return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/fullscreen.svg`;
});

/** This only handles entering fullscreen and not exiting, because there are
 * only native controls in fullscreen */
async function requestFullscreen() {
	try {
		if (!videoElement.value) return;

		if ('webkitSupportsFullscreen' in videoElement.value && videoElement.value.webkitSupportsFullscreen === true) {
			videoElement.value.webkitEnterFullscreen();
		}

		await videoElement.value.requestFullscreen();
	} catch (error) {
		// Nothing to do here
	}
}

function onChangeFullscreen() {
	const change = Boolean(document.fullscreenElement) ? 'entered' : 'exited';
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'toggle_fullscreen',
			value: change === 'entered' ? 1 : 0,
		},
		[titleContext.value]
	);
}

// Campaigns that should use the lighter gradient
const lighterGradientCampaigns = ['PRIMATE_AU_BURST1_SRA'];
/**  Boolean value to determine if we should use the lighter gradient for the video overlay */
const useLighterGradient = computed(() => lighterGradientCampaigns.includes(props.sponsoredAd.campaign?.name ?? ''));
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins.scss';

.sr {
	position: relative;
	container: sponsoredRecommendation / inline-size;
	width: 100%;

	cursor: pointer;

	display: flex;
	flex-direction: column;
	overflow: hidden;

	@media (min-width: $screen-sm) {
		display: block;
	}

	&__container {
		display: flex;
		flex-direction: column;
		z-index: 2;

		padding: 0;
		@media (min-width: $screen-sm) {
			padding: 24px;
		}
	}

	.align-content {
		align-self: center;

		@media (min-width: $screen-sm) {
			align-self: flex-start;
		}
	}

	.provider-icon {
		padding: 24px;
		padding-bottom: 16px;
	}

	&__content {
		display: flex;
		flex-direction: column;
		align-items: center;
		height: min-content;

		gap: 16px;
		@media (min-width: $screen-sm) {
			flex-direction: row;
			align-items: normal;
			gap: 0;
		}

		@media (min-width: $screen-md) {
			gap: 24px;
		}

		.video-controls {
			visibility: hidden;
			@media (min-width: $screen-sm) {
				visibility: visible;
			}

			z-index: 2;
			position: absolute;
			right: 24px;
			bottom: 24px;

			display: flex;
			gap: 16px;
			align-items: center;

			button {
				all: unset;

				img {
					fill: white;
				}
			}
		}
	}

	&__titlePoster {
		z-index: 1;
		overflow: hidden;

		// styles starting from mobile with poster blurred in the back
		visibility: visible;
		width: 100%;
		height: 100%;
		position: absolute;
		z-index: -1;
		top: 0;
		margin: 0;
		filter: blur(30px);

		@media (min-width: $screen-sm) {
			position: relative;
			width: 0;
		}

		// medium+ we show the poster again
		@media (min-width: $screen-md) {
			width: auto;
			filter: none;
			z-index: unset;
		}

		:deep(.picture-comp > img) {
			width: 100%;
			height: 100%;

			// setting height from small onwards so SR keeps height
			@media (min-width: $screen-sm) {
				height: var(--jw-size-23);
			}

			// medium+ we show the poster again
			@media (min-width: $screen-md) {
				width: auto;
			}
		}
	}

	&__titleContent {
		z-index: 1;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;

		padding: 24px;
		padding-top: 0px;

		@media (min-width: $screen-sm) {
			padding: 0;
		}
	}

	&__video-container {
		z-index: 0;

		position: relative;
		display: grid;
		grid-template: 1fr / 1fr;
		grid-template-areas: 'overlap';
		align-content: center;
		aspect-ratio: 16 / 9;
		height: 100%;
		width: 100%;

		// have it come before the campaign info and CTAs
		order: -1;

		@media (min-width: $screen-sm) {
			position: absolute;
			top: 0;
			right: 0;
			width: 60%;
			border-radius: 0 0 var(--jw-radius-small) var(--jw-radius-small);
		}

		&__video {
			grid-area: overlap;
			height: 100%;
			object-fit: cover;
		}

		&::before {
			content: '';
			grid-area: overlap;
			z-index: 1;
			pointer-events: none; /* Makes sure overlay doesn't interfere with interactions */

			@media (min-width: $screen-sm) {
				// See: https://larsenwork.com/easing-gradients/
				background: linear-gradient(
					to right,
					rgba(#111924, 1) 0%,
					rgba(#111924, 0.987) 6.4%,
					rgba(#111924, 0.951) 11.6%,
					rgba(#111924, 0.896) 16.1%,
					rgba(#111924, 0.825) 19.9%,
					rgba(#111924, 0.741) 23.5%,
					rgba(#111924, 0.648) 27%,
					rgba(#111924, 0.55) 30.7%,
					rgba(#111924, 0.45) 34.9%,
					rgba(#111924, 0.352) 39.9%,
					rgba(#111924, 0.259) 45.9%,
					rgba(#111924, 0.175) 53.1%,
					rgba(#111924, 0.104) 61.9%,
					rgba(#111924, 0.049) 72.4%,
					rgba(#111924, 0.013) 85%,
					rgba(#111924, 0) 100%
				);
			}
		}

		&.lighter-gradient::before {
			@media (min-width: $screen-sm) {
				// See: https://larsenwork.com/easing-gradients/
				background: linear-gradient(
					to right,
					rgba(#111924, 1) 0%,
					rgba(#111924, 0.987) 6.4%,
					rgba(#111924, 0.851) 11.6%,
					rgba(#111924, 0.796) 16.1%,
					rgba(#111924, 0.725) 19.9%,
					rgba(#111924, 0.641) 23.5%,
					rgba(#111924, 0.548) 27%,
					rgba(#111924, 0.45) 30.7%,
					rgba(#111924, 0.352) 34.9%,
					rgba(#111924, 0.259) 39.9%,
					rgba(#111924, 0.175) 45.9%,
					rgba(#111924, 0.104) 53.1%,
					rgba(#111924, 0.049) 61.9%,
					rgba(#111924, 0.003) 72.4%,
					rgba(#111924, 0) 80%
				);
			}
		}

		.video-controls {
			visibility: visible;
			@media (min-width: $screen-sm) {
				visibility: hidden;
			}
		}
	}

	&__title {
		font-weight: 700;
		font-size: 24px;
		line-height: 1.2;
		white-space: pre-line;
	}

	&__titleInfo {
		display: flex;
		flex-direction: column;
	}

	&__promoText {
		font-size: 16px;
	}

	&__disclaimer {
		font-size: 12px;
	}
}

//remove opacity on ios devices as it causes flickering
html.ios .sr:active {
	opacity: unset;
}
</style>
